import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Blocks, Monitor, Code, Gamepad2, Terminal, Bot, ChevronRight } from "lucide-react";

const programs = [
  {
    name: "Foundations of Logic & Coding",
    ages: "Ages 6–7",
    step: "Stage 1",
    desc: "Your child develops logical thinking and learns core programming concepts through visual blocks, puzzles, and interactive games.",
    icon: Blocks,
    color: "#1677FF",
    bgColor: "#EAF2FF",
    borderColor: "#1677FF",
  },
  {
    name: "Computer & Digital Skills",
    ages: "Ages 7–9",
    step: "Stage 2",
    desc: "Children learn how computers work, practice digital safety, and create their first simple digital projects with confidence.",
    icon: Monitor,
    color: "#F28C38",
    bgColor: "#FFF1E6",
    borderColor: "#F28C38",
  },
  {
    name: "Visual Programming with Scratch",
    ages: "Ages 8–10",
    step: "Stage 3",
    desc: "Students build animations and their own games while mastering programming logic, loops, and algorithms in Scratch.",
    icon: Code,
    color: "#00A676",
    bgColor: "#E7FFF6",
    borderColor: "#00A676",
  },
  {
    name: "Game Design & Development",
    ages: "Ages 10–12",
    step: "Stage 4",
    desc: "Kids design complete games with characters, mechanics, and levels — learning storytelling and creative problem-solving.",
    icon: Gamepad2,
    color: "#9333EA",
    bgColor: "#F3E8FF",
    borderColor: "#9333EA",
  },
  {
    name: "Python Programming",
    ages: "Ages 11–14",
    step: "Stage 5",
    desc: "Students move to real-world coding with Python, building practical projects, mini-games, and their first applications.",
    icon: Terminal,
    color: "#D52B1E",
    bgColor: "#FFE9E7",
    borderColor: "#D52B1E",
  },
  {
    name: "Robotics & STEM Projects",
    ages: "Ages 12–15",
    step: "Stage 6",
    desc: "Kids build and program robots while applying engineering principles, physics basics, and advanced problem-solving skills.",
    icon: Bot,
    color: "#0891B2",
    bgColor: "#E0F7FA",
    borderColor: "#0891B2",
  },
];

export const ProgramsBento = () => {
  return (
    <section data-testid="programs-section" className="pt-[35px] pb-[35px] md:py-24 bg-white">
      <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold bg-[#FFF1E6] text-[#F28C38] mb-4">
            Structured Learning
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4">
            Our Learning{" "}
            <span className="text-[#1677FF]">Path</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-xl lg:max-w-lg mx-auto">
            A clear educational progression from first puzzles to real programming — each stage builds on the last as your child grows.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map((program, i) => (
            <motion.div
              key={program.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              data-testid={`program-card-${i}`}
              className="relative bg-white rounded-2xl border border-border shadow-[var(--shadow-soft)] card-hover overflow-hidden"
            >
              {/* Color accent band */}
              <div className="h-1.5 w-full" style={{ backgroundColor: program.borderColor }}></div>

              <div className="p-[20px]">
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: program.bgColor }}
                  >
                    <program.icon className="w-7 h-7" style={{ color: program.color }} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{program.step}</span>
                    <Badge variant="secondary" className="text-xs font-semibold rounded-full">
                      {program.ages}
                    </Badge>
                  </div>
                </div>

                <h3 className="text-lg font-bold mb-2">{program.name}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5 w-full">
                  {program.desc}
                </p>

                <Link to="/programs" className="inline-flex items-center gap-1 text-sm font-semibold hover:gap-2 transition-all" style={{ color: program.color }}>
                  Learn More <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link to="/programs">
            <Button className="rounded-full bg-[#1677FF] hover:bg-[#0F63DA] text-white shadow-[var(--btn-shadow)] btn-lift px-8 h-12 text-base font-semibold">
              View All Programs
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ProgramsBento;
